// src/hooks/useDataFetching.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * Custom hook for data fetching with loading states and error handling
 * @param {Function} fetchFn - Function that returns a promise with data
 * @param {Object} options - Configuration options
 * @returns {Object} Data fetching state and handlers
 */
export const useDataFetching = (fetchFn, options = {}) => {
  const {
    immediate = true,
    dependencies = [],
    onSuccess,
    onError,
    transform,
    initialData = null
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchData = useCallback(async (params = {}) => {
    if (!fetchFn) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setLoading(true);
      setError(null);

      const result = await fetchFn({ ...params, signal });
      
      if (!isMountedRef.current || signal.aborted) return;

      let processedData = result;
      if (transform && typeof transform === 'function') {
        processedData = transform(result);
      }

      setData(processedData);
      setLastFetch(new Date());
      
      if (onSuccess) {
        onSuccess(processedData, result);
      }

      return processedData;
    } catch (err) {
      if (!isMountedRef.current || signal.aborted) return;
      
      let errorObj;
      if (err instanceof SyntaxError) {
        errorObj = {
          message: 'The server returned an invalid response. Please try again later.',
          status: 'JSON Parse Error',
          original: err
        };
      } else {
        errorObj = {
          message: err.message || 'An error occurred',
          status: err.status,
          code: err.code,
          original: err
        };
      }
      
      setError(errorObj);
      
      if (onError) {
        onError(errorObj);
      }
      
      throw errorObj;
    } finally {
      if (isMountedRef.current && !signal.aborted) {
        setLoading(false);
      }
    }
  }, [fetchFn, transform, onSuccess, onError]);

  // Memoize dependencies to ensure stable array
  const stableDependencies = useMemo(() => dependencies, dependencies);

  // Auto-fetch on mount and dependency changes
  useEffect(() => {
    if (immediate && fetchFn) {
      fetchData();
    }
  }, [immediate, fetchData, stableDependencies]);

  const refetch = useCallback((params) => {
    return fetchData(params);
  }, [fetchData]);

  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setLoading(false);
    setLastFetch(null);
  }, [initialData]);

  return {
    data,
    loading,
    error,
    lastFetch,
    fetchData,
    refetch,
    reset,
    isStale: lastFetch && (Date.now() - lastFetch.getTime()) > 300000, // 5 minutes
    hasData: data !== null && data !== undefined,
    hasError: error !== null
  };
};

/**
 * Hook for paginated data fetching
 * @param {Function} fetchFn - Function that returns paginated data
 * @param {Object} options - Configuration options
 * @returns {Object} Paginated data state and handlers
 */
export const usePaginatedFetching = (fetchFn, options = {}) => {
  const {
    initialPage = 1,
    initialPageSize = 10,
    ...fetchOptions
  } = options;

  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const paginatedFetchFn = useCallback(async (params = {}) => {
    const result = await fetchFn({
      page,
      pageSize,
      ...params
    });

    // Extract pagination info from result
    if (result && typeof result === 'object') {
      if (result.totalItems !== undefined) setTotalItems(result.totalItems);
      if (result.totalPages !== undefined) setTotalPages(result.totalPages);
      if (result.total !== undefined) setTotalItems(result.total);
      if (result.pages !== undefined) setTotalPages(result.pages);
    }

    return result;
  }, [fetchFn, page, pageSize]);

  // Memoize dependencies to prevent hook order issues
  const paginatedDependencies = useMemo(() => [
    page, 
    pageSize, 
    ...(fetchOptions.dependencies || [])
  ], [page, pageSize, fetchOptions.dependencies]);

  const {
    data,
    loading,
    error,
    fetchData,
    refetch,
    reset: resetData,
    ...rest
  } = useDataFetching(paginatedFetchFn, {
    ...fetchOptions,
    dependencies: paginatedDependencies
  });

  const goToPage = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  }, [totalPages]);

  const changePageSize = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  }, []);

  const nextPage = useCallback(() => {
    if (page < totalPages) {
      setPage(prev => prev + 1);
    }
  }, [page, totalPages]);

  const prevPage = useCallback(() => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  }, [page]);

  const reset = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
    setTotalItems(0);
    setTotalPages(0);
    resetData();
  }, [initialPage, initialPageSize, resetData]);

  return {
    data,
    loading,
    error,
    fetchData,
    refetch,
    reset,
    
    // Pagination state
    page,
    pageSize,
    totalItems,
    totalPages,
    
    // Pagination handlers
    goToPage,
    changePageSize,
    nextPage,
    prevPage,
    
    // Computed values
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1,
    startItem: (page - 1) * pageSize + 1,
    endItem: Math.min(page * pageSize, totalItems),
    
    ...rest
  };
};

/**
 * Hook for infinite scroll data fetching
 * @param {Function} fetchFn - Function that returns paginated data
 * @param {Object} options - Configuration options
 * @returns {Object} Infinite scroll state and handlers
 */
export const useInfiniteScroll = (fetchFn, options = {}) => {
  const {
    initialPageSize = 20,
    ...fetchOptions
  } = options;

  const [allData, setAllData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const infiniteFetchFn = useCallback(async (params = {}) => {
    const result = await fetchFn({
      page,
      pageSize: initialPageSize,
      ...params
    });

    return result;
  }, [fetchFn, page, initialPageSize]);

  const {
    data,
    loading,
    error,
    fetchData,
    reset: resetData,
    ...rest
  } = useDataFetching(infiniteFetchFn, {
    ...fetchOptions,
    dependencies: [page, ...(fetchOptions.dependencies || [])],
    onSuccess: (newData) => {
      if (page === 1) {
        setAllData(newData.items || newData.data || newData);
      } else {
        setAllData(prev => [...prev, ...(newData.items || newData.data || newData)]);
      }
      
      // Check if there's more data
      const items = newData.items || newData.data || newData;
      setHasMore(items.length === initialPageSize);
      setLoadingMore(false);
      
      if (fetchOptions.onSuccess) {
        fetchOptions.onSuccess(newData);
      }
    }
  });

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      setLoadingMore(true);
      setPage(prev => prev + 1);
    }
  }, [loadingMore, hasMore, loading]);

  const reset = useCallback(() => {
    setAllData([]);
    setPage(1);
    setHasMore(true);
    setLoadingMore(false);
    resetData();
  }, [resetData]);

  const refresh = useCallback(() => {
    setPage(1);
    setHasMore(true);
    setLoadingMore(false);
    fetchData();
  }, [fetchData]);

  return {
    data: allData,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
    reset,
    
    // Computed values
    totalLoaded: allData.length,
    isEmpty: allData.length === 0 && !loading,
    
    ...rest
  };
};